import { createSiweMessage } from 'viem/siwe'

interface RedemptionAlertInput {
  amount: string
  contact: string
  liquidity: string
  message: string
  percentOfPosition: string
  position: string
  shortfall: string
  vault: string
  walletAddress: string
}

interface RedemptionMessageInput {
  address: `0x${string}`
  amount: string
  chainId: number
  expirationTime: Date
  host: string
  issuedAt: Date
  nonce: string
  vault: string
}

// A nonce from GET /api/request-redemption is valid for this long. The signed
// message expires at the same time, so a signature cannot be used later.
export const REDEMPTION_NONCE_TTL_MS = 10 * 60 * 1000

function isLocalHost(host: string): boolean {
  return /^(localhost|127\.0\.0\.1)(:\d+)?$/.test(host)
}

// Sign-In with Ethereum (EIP-4361) text for a redemption request. Wallets parse
// this format, show the requesting domain, and warn when it does not match the
// page. The browser and the API route build it from the same fields, so the
// server never trusts a message string from the client.
export function buildRedemptionMessage({
  address,
  amount,
  chainId,
  expirationTime,
  host,
  issuedAt,
  nonce,
  vault,
}: RedemptionMessageInput): string {
  return createSiweMessage({
    address,
    chainId,
    domain: host,
    expirationTime,
    issuedAt,
    nonce,
    statement: `Request a redemption of ${amount} ${vault} from the SeaFi ${vault} vault. This only notifies the SeaFi team. It does not move funds, approve tokens, or cost gas.`,
    uri: `${isLocalHost(host) ? 'http' : 'https'}://${host}/api/request-redemption`,
    version: '1',
  })
}

// Share of `whole` as a percent with two decimals, rounded down, so a full
// exit shows 100.00 and a tiny request never rounds up.
export function formatPercent(part: bigint, whole: bigint): string {
  if (whole <= 0n) return '0.00'
  const basisPoints = (part * 10000n) / whole
  return `${basisPoints / 100n}.${(basisPoints % 100n).toString().padStart(2, '0')}`
}

function escapeTelegramHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function buildRedemptionAlert({
  amount,
  contact,
  liquidity,
  message,
  percentOfPosition,
  position,
  shortfall,
  vault,
  walletAddress,
}: RedemptionAlertInput): string {
  const unit = escapeTelegramHtml(vault)
  return [
    `🟡 <b>Redemption requested</b>`,
    `<b>Requesting:</b> ${escapeTelegramHtml(amount)} ${unit} (${escapeTelegramHtml(percentOfPosition)}% of position)`,
    `<b>Total position:</b> ${escapeTelegramHtml(position)} ${unit}`,
    `<b>Vault liquidity now:</b> ${escapeTelegramHtml(liquidity)} ${unit}`,
    `<b>Liquidity to add:</b> ${escapeTelegramHtml(shortfall)} ${unit}`,
    `<b>Wallet:</b> <code>${escapeTelegramHtml(walletAddress)}</code> (signed)`,
    `<b>Contact:</b> ${contact ? escapeTelegramHtml(contact) : 'not provided'}`,
    message ? `<b>Message:</b> ${escapeTelegramHtml(message)}` : null,
    `<b>Source:</b> vaults.seafi.app`,
  ]
    .filter(Boolean)
    .join('\n')
}
